<template>
	<section class="app-container">
		
		<div class="h1-buttons-back h1-buttons">
			<div class="d-flex align-items-center min-height-title">
				<a
					class="text-secondary cursor-pointer"
					@click="$router.go(-1)"
				>
					<i class="fas fa-arrow-left"></i>
				</a>
				<h1>Invoice Details</h1>
			</div>
			<div class="my-2">
				<select
					v-if="invoiceList.length > 0"
					name="period"
					id="period"
					class="form-control"
					@change="changeInvoice($event)"
				>
					<option
						v-for="inv in invoiceList"
						:key="inv.id"
						:value="inv.id"
						:class="{ 'current-invoice': inv.id === invoice.id }"
					>
						{{ formatDate(inv.createdOn, 'MMMM, YYYY') }}
					</option>
				</select>
			</div>
		</div>
		<ErrorAlert
			v-if="invoice && invoice.paymentError"
			:error="invoicePaymentError"
		/>
		<div class="content-wrapper" v-if="invoice && !loading">
			<div class="seller-card">
				<div class="seller-info">
					<p class="seller-name">HyperAspect</p>
					<p class="seller-postcode">1000</p>
					<p class="seller-address-1">Sofia</p>
					<p class="seller-vat">VAT No: 123456789</p>
				</div>
				<div class="seller-invoice-info">
					<p class="seller-invoice-info-row">
						<span class="grey-text">Invoice</span>
						<span class="main-text">{{ invoice.invoiceId }}</span>
					</p>
					<p class="seller-invoice-info-row">
						<span class="grey-text">Billed on</span>
						<span class="main-text">{{ billedOn }}</span>
					</p>
					<p class="seller-invoice-info-row">
						<span class="grey-text">Billing period</span>
						<span class="main-text">{{ billingPeriod }}</span>
					</p>
					<p class="seller-invoice-info-row">
						<span class="grey-text">Due on</span>
						<span class="main-text">{{ dueOn }}</span>
					</p>
				</div>
			</div>
			<div class="buyer-card">
				<div class="buyer-data">
					<span class="grey-text">Bill to</span>
					<p class="buyer-name">
						{{
							company !== 'n/a' ? company : customerName
						}}
					</p>
					<p class="buyer-postcode">{{ zip }}</p>
					<p class="buyer-address-1">{{ city }}</p>
					<p class="buyer-vat">
						Customer VAT No:
						{{ company !=='n/a' ? vat : 'n/a' }}
					</p>
					<p class="buyer-vat">Card ending with: {{ cardLast4 }}</p>
				</div>
				<div class="buyer-bill">
					<div class="buyer-grand-total">
						<p class="text-right">Grand Total</p>
						<p class="grand-num">
							{{ currencySymbol }} {{ invoiceTotal }}
						</p>
					</div>
					<div class="buyer-vcash" v-if="virtualCashDiscount!=='0.00'">
						<p class="text-right">Free tier</p>
						<p class="grand-num">
							{{ currencySymbol }} -{{ virtualCashDiscount }}
						</p>
					</div>
					<div class="buyer-paid" :class="{'cyan': invoice.paid}">
						<span class="mr-5">{{invoice.paid ? 'Paid' : 'To be paid'}}</span> {{ currencySymbol }} {{ totalPaid }}
					</div>
					<button
						class="btn btn-primary"
						v-if="!invoice.paid"
						@click="payInvoice"
					>
						<span
							v-if="loadingPay"
							class="spinner-border spinner-border-sm mr-2"
							role="status"
							aria-hidden="true"
						></span>
						Pay Now
					</button>
				</div>
			</div>
			<template v-if="invoice.bill">
				<UsageCard
					v-for="(service, index) in invoice.bill.services"
					:service="service"
					:key="index"
					:allMetrics="true"
				/>
			</template>
		</div>
		<ErrorAlert v-if="error && !loading" :error="error" />
		<NoData v-if="!invoice && !error && !loading" />
		<ErrorDialog ref="errDialog" />
		<Spinner v-if="loading" />
	</section>
</template>

<script>
import Invoice from '../../services/invoice';
import ErrorAlert from '.././shared/ErrorAlert';
import ErrorDialog from '.././shared/ErrorDialog';
import Spinner from '.././shared/Spinner';
import NoData from '.././shared/NoData';
import dayjs from 'dayjs';
import UsageCard from '../usage/UsageCard';
import { currencyMap } from '../../util/currency-symbol-map';

export default {
	name: 'InvoiceDetails',
	components: {
		Spinner,
		ErrorDialog,
		NoData,
		UsageCard,
		ErrorAlert,
	},
	data() {
		return {
			invoice: null,
			error: null,
			loading: false,
			loadingPay: false,
			invoiceList: [],
		};
	},
	mounted() {
		this.loadInvoice();
		this.fetchInvoiceList();
	},
	methods: {
		loadInvoice() {
			let id = this.$route.query.id;
			if (!id) return;
			this.fetchInvoice(id);
		},
		changeInvoice(event) {
			let id = event.target.value;
			if (!id) return;
			this.fetchInvoice(id);
		},
		fetchInvoice(id) {
			this.loading = true;
			Invoice.instance()
				.getInvoiceById(id)
				.then((res) => {
					this.loading = false;
					this.error = null;
					this.invoice = res.data;
				})
				.catch((err) => {
					this.loading = false;
					this.error = err;
				});
		},
		fetchInvoiceList() {
			Invoice.instance()
				.getInvoices({ page: 0, size: 50 })
				.then((res) => {
					this.invoiceList = res.data.content;
				})
				.catch((err) => {
					this.$refs.errDialog.show(err);
				});
		},
		formatDate(date, format) {
			if (!date || !format) return;
			let dayjsObj = dayjs(date);
			return dayjsObj.format(format);
		},
		payInvoice() {
			this.loadingPay = true;
			Invoice.instance()
				.payInvoice(this.invoice.id)
				.then(() => {
					this.loadingPay = false;
					this.error = null;
					this.fetchInvoice(this.invoice.id);
				})
				.catch((err) => {
					this.loadingPay = false;
					this.$refs.errDialog.show(err);
					this.error = err;
				});
		},
	},
	computed: {
		company: function () {
			return this.invoice?.address?.companyName || 'n/a';
		},
		zip: function () {
			return this.invoice?.address?.addressZip || 'n/a';
		},
		city: function () {
			return this.invoice?.address?.addressCity || 'n/a';
		},
		vat: function () {
			return this.invoice?.address?.companyVat || 'n/a';
		},
		billedOn: function () {
			let dateVal = this.invoice?.createdOn;
			if (!dateVal) return 'n/a';
			let dayjsObj = dayjs(dateVal);

			return dayjsObj.format('MMMM DD, YYYY');
		},
		billingPeriod: function () {
			if (this.invoice.period) {
				return (
					dayjs(this.invoice.period.start).format('MMM DD') +
					' - ' +
					dayjs(this.invoice.period.end).format('MMM DD')
				);
			} else {
				return 'n/a';
			}
		},
		dueOn: function () {
			let dateVal = this.invoice?.dueDate;
			if (!dateVal) return 'n/a';
			let dayjsObj = dayjs(dateVal);

			return dayjsObj.format('MMMM DD, YYYY');
		},
		customerName: function () {
			return this.invoice?.card?.name || 'n/a';
		},
		cardLast4: function () {
			return this.invoice?.card?.last4 || 'n/a';
		},
		invoiceTotal() {
			let totalCents = this.invoice?.bill?.grandTotalCents;
			if (!totalCents && totalCents != 0) return 'n/a';
			return (totalCents / 100).toFixed(2);
		},
		virtualCashDiscount() {
			let vCash = this.invoice.virtualCashDiscount;
			return (vCash / 100).toFixed(2);
		},
		totalPaid() {
			let vCash = this.invoice.virtualCashDiscount,
				totalCents = this.invoice?.bill?.grandTotalCents;
			return ((totalCents - vCash) / 100).toFixed(2);
		},
		currencySymbol() {
			let code = this.invoice?.currency;
			if (!code) return 'n/a';
			let upperCode = code.toUpperCase();
			return currencyMap[upperCode];
		},
		invoicePaymentError() {
			return new Error(
				'Payment failed with the following error: ' +
					this.invoice.paymentError,
			);
		},
	},
};
</script>

<style scoped>
.max-width-container {
	max-width: 1024px;
}
.seller-card {
	margin-bottom: 2rem;
}
.seller-card,
.buyer-card {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-size: 1.4rem;
	box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.2);
	padding: 2rem 2.5rem;
	border-radius: 2px;
}
.seller-name {
	font-size: 1.6rem;
	font-weight: bold;
}
.seller-invoice-info-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
}
.grey-text {
	color: #919191;
}
.seller-invoice-info-row .grey-text {
	margin-right: 5rem;
}
.buyer-bill {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	justify-content: space-between;
}
.buyer-grand-total {
	display: flex;
	padding-right: 1.2rem;
}
.buyer-vcash {
	display: flex;
	padding-right: 1.2rem;
}
.grand-num {
	font-size: 1.4rem;
	font-weight: bold;
	color: #3d3d3d;
	margin-bottom: 0;
	min-width: 100px;
    text-align: right;
}
.cursor-pointer:hover {
	cursor: pointer;
}
.min-height {
	min-height: 250px;
	height: 90%;
	overflow: auto;
	width: 100%;
	padding-right: 40%;
}
.current-invoice {
	background-color: #919191;
	font-weight: bold;
}
.min-height-title {
	min-height: 4.7rem;
}
.chips-secondary {
	background-color: #fff;
	font-size: 1.3rem;
	border-radius: 16px;
	border: 1px solid #212529;
	color: rgba(33, 37, 49, 0.87);
	padding: 0px 32px;
	margin: 8px 0;
}
.no-cursor:hover {
	cursor: default;
}
.cyan {
	background-color: rgba(176, 247, 192, 0.4);
}
.buyer-paid {
	font-size: 2rem;
	font-weight: bold;
	padding: 0.5rem 1.2rem;
}
</style>